import { useCallback, useState } from 'react';
import { useSelectedModuleForSettings, useSetSelectedModuleForSettings } from './useEditCourse';
import { usePanelState } from './usePanelState';
import { notifyError } from '../../../services/notifications';

/**
 * useEditCourseUIState
 *
 * Composition hook for EditCourse UI wiring:
 * - left/right panel state (via usePanelState)
 * - selected module for settings (Redux)
 * - content modal open/close + pending new module state
 * - settings panel open/close behavior (desktop vs mobile drawer)
 *
 */
export function useEditCourseUIState({ breakpoints, menuEditLeftMode, setMenuEditLeftMode, setMenuEditRightToggle, PANEL_MODES }) {
  const panels = usePanelState({ breakpoints, menuEditLeftMode, setMenuEditLeftMode, PANEL_MODES });

  // Module editing states
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [pendingNewModule, setPendingNewModule] = useState(null);
  const [deleteModuleDialog, setDeleteModuleDialog] = useState({ open: false, module: null });

  const selectedModule = useSelectedModuleForSettings();
  const setSelectedModule = useSetSelectedModuleForSettings();

  const openModuleContentModal = useCallback(
    (module) => {
      setSelectedModule(module);
      setIsContentModalOpen(true);
    },
    [setSelectedModule]
  );

  const closeModuleContentModal = useCallback(() => {
    setIsContentModalOpen(false);
    setSelectedModule(null);
    setPendingNewModule(null);
  }, [setSelectedModule]);

  const insertModule = useCallback((moduleType, position) => {
    const tempModule = {
      type: { name: moduleType },
      data: {},
      _isNew: true,
      _position: position,
    };

    setPendingNewModule(tempModule);
    setIsContentModalOpen(true);
  }, []);

  const editModuleSettings = useCallback(
    (module) => {
      // If clicking the same module that's already selected, close the panel
      if (selectedModule?.id === module.id) {
        setSelectedModule(null);
        panels.closeRightPanel();
        setMenuEditRightToggle(false);
        return;
      }

      // Otherwise, select the new module and open the panel
      setSelectedModule(module);

      if (panels.isMobileScreen()) {
        // On mobile, open the mobile drawer
        setMenuEditRightToggle(true);
        return;
      }

      // On medium screens, compact the left panel first to allow right panel to open
      if (panels.isMediumScreen()) {
        panels.compactLeftPanel();
      }

      // Open the right panel (desktop/tablet)
      panels.openRightPanel();
    },
    [panels, selectedModule, setMenuEditRightToggle, setSelectedModule]
  );

  const closeSettingsPanel = useCallback(() => {
    setSelectedModule(null);
    panels.closeRightPanel();
    setMenuEditRightToggle(false); // Also close mobile drawer
  }, [panels, setMenuEditRightToggle, setSelectedModule]);

  const saveSettingsAndClearSelection = useCallback(
    async (module, data, saveModuleSettings) => {
      if (!saveModuleSettings) return;

      try {
        await saveModuleSettings(module, data);

        // Keep panel open but clear selection
        setSelectedModule(null);
      } catch (error) {
        console.error('Failed to update module settings:', error);
        notifyError('Failed to save settings. Please try again.');
      }
    },
    [setSelectedModule]
  );

  const openDeleteModuleDialog = useCallback((module) => {
    setDeleteModuleDialog({ open: true, module });
  }, []);

  const closeDeleteModuleDialog = useCallback(() => {
    setDeleteModuleDialog({ open: false, module: null });
  }, []);

  const confirmDeleteModule = useCallback(
    async (deleteModule) => {
      const module = deleteModuleDialog.module;
      if (!deleteModule || !module) return;

      try {
        const result = await deleteModule(module);
        if (!result?.deleted) return;

        // Close settings panel if this module was selected
        if (selectedModule?.id === module.id) {
          setSelectedModule(null);
          panels.closeRightPanel();
          setMenuEditRightToggle(false);
        }
      } catch (error) {
        console.error('Failed to delete module:', error);
        notifyError('Failed to delete module. Please try again.');
      } finally {
        closeDeleteModuleDialog();
      }
    },
    [closeDeleteModuleDialog, deleteModuleDialog.module, panels, selectedModule, setMenuEditRightToggle, setSelectedModule]
  );

  return {
    // Panels
    isLeftPanelCompact: panels.isLeftPanelCompact,
    isRightPanelOpen: panels.isRightPanelOpen,
    toggleLeftPanel: panels.toggleLeftPanel,
    toggleLeftPanelMode: panels.toggleLeftPanelMode,
    toggleRightPanel: panels.toggleRightPanel,
    compactLeftPanel: panels.compactLeftPanel,
    openRightPanel: panels.openRightPanel,
    closeRightPanel: panels.closeRightPanel,
    isMobileScreen: panels.isMobileScreen,
    isMediumScreen: panels.isMediumScreen,

    // Settings selection
    selectedModule,
    setSelectedModule,
    editModuleSettings,
    closeSettingsPanel,
    saveSettingsAndClearSelection,

    // Delete module dialog
    deleteModuleDialog,
    openDeleteModuleDialog,
    closeDeleteModuleDialog,
    confirmDeleteModule,

    // Content modal
    isContentModalOpen,
    pendingNewModule,
    openModuleContentModal,
    closeModuleContentModal,
    insertModule,
  };
}
